import { Check, ChevronsUpDown, Search } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Single-select combobox with type-to-filter.
 *
 * Built on Popover plus a filtered list rather than pulling in cmdk: the
 * portal already ships @radix-ui/react-popover, and a dependency is a poor
 * trade for a list this size. Radix's Select is the wrong control here
 * because it has no text filter.
 *
 * Keyboard contract, implemented explicitly since there is no cmdk to
 * inherit it from: opening focuses the search box, Up/Down move the active
 * option, Enter commits it, Escape closes.
 *
 * The ARIA combobox role lives on the SEARCH INPUT, not on the trigger button.
 * `aria-activedescendant` is only honoured on an element that is itself a
 * combobox/listbox, so splitting it from `role="combobox"` (leaving the role on
 * the trigger) makes screen readers ignore the active option entirely. The
 * input therefore carries role, aria-expanded, aria-autocomplete, aria-controls
 * and aria-activedescendant together; the trigger is just aria-haspopup.
 */
export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: ReadonlyArray<SearchableSelectOption>;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Fold a string for comparison: strip diacritics and lowercase.
 *
 * Without this a Spanish-speaking user typing "itau" or "republica" gets no
 * match for "Itaú" / "República Microfinanzas" and is pushed into the free-text
 * "other" branch -- which fragments exactly the vocabulary this list exists to
 * keep groupable.
 */
function foldForSearch(input: string): string {
  return input
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

export function SearchableSelect({
  id,
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  disabled = false,
  className,
}: SearchableSelectProps): JSX.Element {
  // Fall back to a generated id: the aria wiring below is not optional, and
  // keying it off an optional prop would silently drop it when `id` is omitted.
  const reactId = useId();
  const baseId = id ?? reactId;
  const listboxId = `${baseId}-listbox`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label ?? "",
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = foldForSearch(query);
    if (!q) return options;
    return options.filter((o) => foldForSearch(o.label).includes(q));
  }, [options, query]);

  // Reset the search each time the popover opens so a stale filter never
  // hides the full list, and keep the active row inside the filtered range.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  // Keyed on the query, not just on filtered.length: a new query that happens
  // to return the same number of rows would otherwise leave activeIndex pointing
  // at an unrelated option, and Enter would commit the wrong one.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const commit = (next: string): void => {
    onValueChange(next);
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => (filtered.length === 0 ? 0 : (prev + 1) % filtered.length));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) =>
        filtered.length === 0 ? 0 : (prev - 1 + filtered.length) % filtered.length,
      );
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const option = filtered[activeIndex];
      if (option) commit(option.value);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between bg-card font-normal",
            !selectedLabel && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            role="combobox"
            aria-expanded
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={
              filtered[activeIndex] ? `${baseId}-opt-${filtered[activeIndex].value}` : undefined
            }
            className="h-10 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          // Rows use onMouseMove, not onMouseEnter: arrow-key navigation scrolls
          // the list under a stationary cursor, and mouseenter would fire on the
          // row that slid under the pointer and steal the keyboard selection.
          className="max-h-60 overflow-y-auto p-1"
        >
          {filtered.length === 0 ? (
            // py-12 default would blow out a 240px dropdown; className exists for this.
            <EmptyState title={emptyLabel} className="gap-1 py-6" />
          ) : (
            filtered.map((option, index) => {
              const selected = option.value === value;
              const active = index === activeIndex;
              return (
                <button
                  key={option.value}
                  id={`${baseId}-opt-${option.value}`}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-active={active}
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => commit(option.value)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors",
                    active ? "bg-muted text-foreground" : "text-foreground",
                  )}
                >
                  <Check
                    className={cn("h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")}
                  />
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
